/**
 * Smart inventory search parser (v5.5 Phase C).
 *
 * Translates free-text "show me crew cab silverado under 30k" into a
 * structured filter delta + the residual search text.
 *
 * Returns: {filters: {...}, residual_search: string, applied_tokens: string[]}
 *
 * Filters keys mirror the backend /api/vehicles query params.
 */

// Common misspelling → canonical model token.
const MISSPELL_MAP = {
  equiox: "equinox",
  equnix: "equinox",
  equinx: "equinox",
  sliverado: "silverado",
  silverato: "silverado",
  silverodo: "silverado",
  silvarado: "silverado",
  taho: "tahoe",
  tahoo: "tahoe",
  travese: "traverse",
  travers: "traverse",
  trabverse: "traverse",
  corvet: "corvette",
  corvete: "corvette",
  chevey: "chevrolet",
  chevvy: "chevrolet",
  chev: "chevrolet",
  trax: "trax",
  blzer: "blazer",
};

const MONEY_RE = /\$?\s*([0-9]+(?:\.[0-9]+)?)\s*(k|thousand)?/i;
const UNDER_PRICE_RE = /under\s*\$?\s*([0-9]+(?:\.[0-9]+)?)\s*(k|thousand)?/i;
const UNDER_MILEAGE_RE = /under\s*([0-9]+(?:\.[0-9]+)?)\s*(k)?\s*mi(les)?/i;
const PAYMENT_RE = /payment\s*(?:under)?\s*\$?\s*([0-9]+)/i;

function normalizeAmount(numStr, kFlag) {
  let n = parseFloat(numStr);
  if (isNaN(n)) return null;
  if (kFlag) n *= 1000;
  // If number <= 99 and no k flag, assume "k" implied for price context.
  return Math.round(n);
}

export function parseSmartSearch(rawText) {
  const out = {
    filters: {},
    residual_search: "",
    applied_tokens: [],
  };
  if (!rawText) return out;

  // 1. Normalize text: lowercase + collapse whitespace
  let text = " " + String(rawText).toLowerCase().replace(/\s+/g, " ").trim() + " ";

  // 2. Fix misspellings
  for (const [bad, good] of Object.entries(MISSPELL_MAP)) {
    const rx = new RegExp(`\\b${bad}\\b`, "g");
    if (rx.test(text)) {
      text = text.replace(rx, good);
      out.applied_tokens.push(`corrected:${bad}→${good}`);
    }
  }

  // 3. Body style keywords
  if (/\bsuv?s?\b|sport utility|crossover/.test(text)) {
    out.filters.body_style = "suv";
    out.applied_tokens.push("body:suv");
    text = text.replace(/\bsuvs?\b|sport utility|crossover/g, " ");
  }
  if (/\btrucks?\b|\bpickup\b/.test(text)) {
    out.filters.body_style = "truck";
    out.applied_tokens.push("body:truck");
    text = text.replace(/\btrucks?\b|\bpickup\b/g, " ");
  }
  if (/\bvans?\b|\bminivans?\b/.test(text)) {
    out.filters.body_style = "van";
    out.applied_tokens.push("body:van");
    text = text.replace(/\bvans?\b|\bminivans?\b/g, " ");
  }
  if (/\bevs?\b|\belectric\b|\bev\b/.test(text)) {
    out.filters.fuel_type = "electric";
    out.applied_tokens.push("fuel:electric");
    text = text.replace(/\bevs?\b|\belectric\b/g, " ");
  }
  if (/\bdiesel\b/.test(text)) {
    out.filters.fuel_type = "diesel";
    out.applied_tokens.push("fuel:diesel");
    text = text.replace(/\bdiesel\b/g, " ");
  }

  // 4. Drive / cab patterns
  if (/4x4|four wheel|4wd/.test(text)) {
    out.filters.drivetrain = "4WD";
    out.applied_tokens.push("drive:4wd");
    text = text.replace(/\b4x4\b|four wheel|4wd/g, " ");
  } else if (/\bawd\b|all wheel/.test(text)) {
    out.filters.drivetrain = "AWD";
    out.applied_tokens.push("drive:awd");
    text = text.replace(/\bawd\b|all wheel/g, " ");
  }

  // 5. Cab size
  if (/crew cab/.test(text)) {
    out.filters.cab_size = "crew";
    out.applied_tokens.push("cab:crew");
    text = text.replace(/crew cab/g, " ");
  } else if (/double cab/.test(text)) {
    out.filters.cab_size = "double";
    out.applied_tokens.push("cab:double");
    text = text.replace(/double cab/g, " ");
  } else if (/regular cab/.test(text)) {
    out.filters.cab_size = "regular";
    out.applied_tokens.push("cab:regular");
    text = text.replace(/regular cab/g, " ");
  }

  // 6. Bed length
  if (/long bed/.test(text)) {
    out.filters.bed_length = "long";
    out.applied_tokens.push("bed:long");
    text = text.replace(/long bed/g, " ");
  }

  // 7. Row count
  if (/(third row|3rd row)/.test(text)) {
    out.filters.feature = "third_row";
    out.applied_tokens.push("feature:third_row");
    text = text.replace(/(third row|3rd row)/g, " ");
  }

  // 8. Trim chips: Trail Boss, Z71, High Country
  const trims = [
    ["trail boss", "Trail Boss"],
    ["high country", "High Country"],
    ["z71", "Z71"],
    ["redline", "Redline"],
    ["ltz", "LTZ"],
    ["zl1", "ZL1"],
    ["zr1", "ZR1"],
  ];
  for (const [needle, label] of trims) {
    if (new RegExp(`\\b${needle}\\b`).test(text)) {
      out.filters.trim = label;
      out.applied_tokens.push(`trim:${label}`);
      text = text.replace(new RegExp(`\\b${needle}\\b`, "g"), " ");
      break;
    }
  }

  // 9. Model recognition (Chevy)
  const models = [
    "silverado",
    "colorado",
    "tahoe",
    "suburban",
    "traverse",
    "trailblazer",
    "trax",
    "blazer",
    "equinox",
    "corvette",
    "camaro",
    "malibu",
  ];
  // Body-style implied by model (per Phase C spec — silverado/colorado → truck, etc.).
  const MODEL_BODY = {
    silverado: "truck",
    colorado: "truck",
    tahoe: "suv",
    suburban: "suv",
    traverse: "suv",
    trailblazer: "suv",
    trax: "suv",
    blazer: "suv",
    equinox: "suv",
    corvette: "car",
    camaro: "car",
    malibu: "car",
  };
  for (const m of models) {
    if (new RegExp(`\\b${m}\\b`).test(text)) {
      out.filters.model = m.charAt(0).toUpperCase() + m.slice(1);
      out.applied_tokens.push(`model:${m}`);
      if (!out.filters.body_style && MODEL_BODY[m]) {
        out.filters.body_style = MODEL_BODY[m];
        out.applied_tokens.push(`body:${MODEL_BODY[m]}`);
      }
      text = text.replace(new RegExp(`\\b${m}\\b`, "g"), " ");
      break;
    }
  }
  // HD trucks
  if (/\bhd\b/.test(text) && out.filters.model === "Silverado") {
    out.filters.trim = "HD";
    text = text.replace(/\bhd\b/g, " ");
  }

  // 10. Status (new vs used)
  if (/\bnew\b/.test(text)) {
    out.filters.status = "new";
    out.applied_tokens.push("status:new");
    text = text.replace(/\bnew\b/g, " ");
  } else if (/\bused\b|pre-owned|preowned/.test(text)) {
    out.filters.status = "used";
    out.applied_tokens.push("status:used");
    text = text.replace(/\bused\b|pre-owned|preowned/g, " ");
  }

  // 11. Payment under (run BEFORE generic "under $X")
  const pm = PAYMENT_RE.exec(text);
  if (pm) {
    const amt = parseFloat(pm[1]);
    if (amt) {
      out.filters.max_payment = amt;
      out.applied_tokens.push(`max_payment:${amt}`);
    }
    text = text.replace(PAYMENT_RE, " ");
  }

  // 12. Mileage under (run BEFORE generic "under $X" so "under 20k miles" wins)
  const mm = UNDER_MILEAGE_RE.exec(text);
  if (mm) {
    const k = mm[2] ? 1000 : 1;
    const miles = Math.round(parseFloat(mm[1]) * k);
    if (miles) {
      out.filters.max_mileage = miles;
      out.applied_tokens.push(`max_mileage:${miles}`);
    }
    text = text.replace(UNDER_MILEAGE_RE, " ");
  }

  // 13. Price under
  const um = UNDER_PRICE_RE.exec(text);
  if (um) {
    const amt = normalizeAmount(um[1], um[2] || (parseFloat(um[1]) < 100 ? "k" : null));
    if (amt) {
      out.filters.max_price = amt;
      out.applied_tokens.push(`max_price:${amt}`);
    }
    text = text.replace(UNDER_PRICE_RE, " ");
  }

  // 14. Color hints (basic)
  const colors = ["black", "white", "red", "blue", "silver", "gray", "grey", "green"];
  for (const c of colors) {
    if (new RegExp(`\\b${c}\\b`).test(text)) {
      out.filters.exterior_color = c;
      out.applied_tokens.push(`color:${c}`);
      text = text.replace(new RegExp(`\\b${c}\\b`, "g"), " ");
      break;
    }
  }

  // 15. Reduce residual to non-stopword tokens
  text = text.replace(/\bunder\b|\bover\b|\bwith\b|\bnice\b|\bcheap\b/g, " ").trim();
  out.residual_search = text.replace(/\s+/g, " ").trim();

  return out;
}
